import { Controller } from "@hotwired/stimulus"
import * as d3 from "d3"
import { set as storeSet } from "../lib/session_store"

function _cssVar(name) {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim()
}

const SECTOR_COLOR_PALETTE = [
  "#FF2E92",
  "#6A00FF",
  "#00D4FF",
  "#FFB400",
  "#00FF88",
  "#FF6B00",
]

function getSectorColor(index) {
  return SECTOR_COLOR_PALETTE[index % SECTOR_COLOR_PALETTE.length]
}

// Legacy 3-sector colors (used as fallback)
function getSectorColors() {
  return {
    S2: _cssVar("--color-vivid-purple"),
    S3: _cssVar("--color-electric-blue"),
  }
}

// Shared offscreen canvas for text measurement (avoids getBBox() forced layout)
let _measureCtx = null
function getTextWidth(text, fontSize = 10, fontWeight = "normal") {
  if (!_measureCtx) {
    const canvas = document.createElement("canvas")
    _measureCtx = canvas.getContext("2d")
  }
  _measureCtx.font = `${fontWeight} ${fontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
  return _measureCtx.measureText(text).width
}

export default class extends Controller {
  static targets = ["canvas", "legend"]
  static values = {
    metric: String,
    label: String,
    color: String,
    legend: String,
    step: { type: Boolean, default: false },
    comparison: { type: Boolean, default: false },
    deltaMode: { type: Boolean, default: false },
    displayScale: { type: Number, default: 1 },
    laps: Array,
    delta: Object,
    referenceTelemetry: Array,
    colors: Array,
    sector1Ms: Number,
    sector2Ms: Number,
    sector2Distance: Number,
    sector3Distance: Number,
    sectorsMs: Array,
    sectorDistances: Array,
    axisColor: String,
  }

  connect() {
    this.telemetry = null
    this.comparisonLaps = null
    this.hiddenMetrics = new Set()
    this._zoomDistanceRange = null
    this._xFullExtent = null
    this._isPanning = false
    this._panStartX = null
    this._panStartRange = null
    this._hoverLabels = null
    this._hoverState = null
    // Canvas rendering state
    this._chartCanvas = null
    this._chartCtx = null
    this._dpr = 1
    this._renderState = null
    this._lastCanvasWidth = 0
    this._lastCanvasHeight = 0

    this.handleLoaded = (e) => this._onTelemetryLoaded(e)
    this.handleSyncHover = (e) => this.onHover(e)
    this.handleSyncLeave = () => this.onLeave()
    this.handleSyncZoom = (e) => this._onSyncZoom(e)

    this.element.addEventListener("telemetry:loaded", this.handleLoaded)
    this.element.addEventListener("sync:hover", this.handleSyncHover)
    this.element.addEventListener("sync:leave", this.handleSyncLeave)
    this.element.addEventListener("sync:zoom", this.handleSyncZoom)

    // Window-level pan handlers (survive re-renders)
    this._handlePanMove = (e) => this._onPanMove(e)
    this._handlePanEnd = () => this._onPanEnd()
    window.addEventListener("mousemove", this._handlePanMove)
    window.addEventListener("mouseup", this._handlePanEnd)

    this.resizeObserver = new ResizeObserver((entries) => {
      if (!this.telemetry && !this.comparisonLaps) return
      const entry = entries[0]
      const w = entry.contentRect.width
      const h = entry.contentRect.height
      if (w === 0 || h === 0) return
      if (w === this._lastCanvasWidth && h === this._lastCanvasHeight) return
      this._lastCanvasWidth = w
      this._lastCanvasHeight = h
      this._scheduleRedraw()
    })
    this.resizeObserver.observe(this.canvasTarget)

    this._onAppearanceChange = () => {
      if (this.telemetry || this.comparisonLaps) this._scheduleRender()
    }
    window.addEventListener("appearance:change", this._onAppearanceChange)

    this._showLoading()
  }

  disconnect() {
    if (this._renderRAF) cancelAnimationFrame(this._renderRAF)
    if (this._hoverRAF) cancelAnimationFrame(this._hoverRAF)
    if (this._redrawRAF) cancelAnimationFrame(this._redrawRAF)
    if (this.resizeObserver) this.resizeObserver.disconnect()
    window.removeEventListener("mousemove", this._handlePanMove)
    window.removeEventListener("mouseup", this._handlePanEnd)
    this.element.removeEventListener("telemetry:loaded", this.handleLoaded)
    this.element.removeEventListener("sync:hover", this.handleSyncHover)
    this.element.removeEventListener("sync:leave", this.handleSyncLeave)
    this.element.removeEventListener("sync:zoom", this.handleSyncZoom)
    window.removeEventListener("appearance:change", this._onAppearanceChange)
  }

  _showLoading() {
    this.canvasTarget.innerHTML =
      '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">Loading telemetry&hellip;</div>'
  }

  _onTelemetryLoaded(event) {
    const { telemetry, laps, zoomState } = event.detail

    if (this.comparisonValue && laps) {
      this.comparisonLaps = laps
      this.telemetry = telemetry
    } else {
      this.telemetry = telemetry
    }

    // Adopt zoom state from sync controller if provided
    if (zoomState !== undefined) {
      this._zoomDistanceRange = zoomState
    }

    this._updateLegend()
    this.render()
  }

  _onSyncZoom(event) {
    const { distanceRange } = event.detail
    this._zoomDistanceRange = distanceRange
    this._scheduleRedraw()
  }

  _onPanMove(event) {
    if (!this._isPanning || this._panStartX == null) return
    const dx = event.clientX - this._panStartX
    // Convert pixel delta to distance delta using current scale
    const pixelsPerDist =
      this._innerWidth / (this._panStartRange[1] - this._panStartRange[0])
    const distDelta = -dx / pixelsPerDist

    const [fullMin, fullMax] = this._xFullExtent
    const rangeSize = this._panStartRange[1] - this._panStartRange[0]

    let newD0 = this._panStartRange[0] + distDelta
    let newD1 = this._panStartRange[1] + distDelta
    if (newD0 < fullMin) {
      newD0 = fullMin
      newD1 = fullMin + rangeSize
    }
    if (newD1 > fullMax) {
      newD1 = fullMax
      newD0 = fullMax - rangeSize
    }

    this._zoomDistanceRange = [newD0, newD1]
    this._scheduleRedraw()
    this._dispatchZoom(this._zoomDistanceRange)
  }

  _onPanEnd() {
    if (!this._isPanning) return
    this._isPanning = false
    this._panStartX = null
    this._panStartRange = null
    if (this._overlay) {
      this._overlay.attr(
        "cursor",
        this._zoomDistanceRange ? "grab" : "crosshair",
      )
    }
  }

  render() {
    const container = this.canvasTarget
    container.innerHTML = ""
    this._renderState = null

    // Comparison delta mode
    if (this.comparisonValue && this.deltaModeValue && this.comparisonLaps) {
      this._renderDelta(container)
      return
    }

    // Legacy delta mode (inline data)
    if (
      this.comparisonValue &&
      this.hasDeltaValue &&
      this.hasReferenceTelemetryValue
    ) {
      this._renderDelta(container)
      return
    }

    // Comparison metrics mode: overlaid traces from multiple laps
    if (this.comparisonValue && this.comparisonLaps) {
      this._renderComparisonMetrics(container)
      return
    }

    // Legacy comparison mode (inline data)
    if (this.comparisonValue && this.hasLapsValue) {
      this._renderComparisonMetrics(container)
      return
    }

    // Default: single-lap mode
    this._renderSingle(container)
  }

  // --- Shared helpers ---

  _computeXDomain(data) {
    const fullExtent = d3.extent(data, (d) => d.distance_m)
    this._xFullExtent = fullExtent
    return this._zoomDistanceRange || fullExtent
  }

  _computeXDomainMulti(laps) {
    let xMin = Infinity,
      xMax = -Infinity
    laps.forEach((lap) => {
      const data = lap.telemetry
      if (!data) return
      data.forEach((d) => {
        if (d.distance_m != null) {
          if (d.distance_m < xMin) xMin = d.distance_m
          if (d.distance_m > xMax) xMax = d.distance_m
        }
      })
    })
    if (!isFinite(xMin)) xMin = 0
    if (!isFinite(xMax)) xMax = 1
    this._xFullExtent = [xMin, xMax]
    return this._zoomDistanceRange || [xMin, xMax]
  }

  _setupZoomAndBrush(svg, g, innerWidth, innerHeight, xScale, hoverData) {
    const self = this

    // Smooth mousewheel zoom — lerp toward target each frame
    const lerpFactor = 0.25
    self._zoomTarget = null

    const tickChartZoom = () => {
      self._chartZoomRAF = null
      const target = self._zoomTarget
      if (!target) return
      const cur = self._zoomDistanceRange || self._xFullExtent
      const [fullMin, fullMax] = self._xFullExtent

      if (target === "reset") {
        self._zoomDistanceRange = null
        self._zoomTarget = null
        self._scheduleRedraw()
        self._dispatchZoom(null)
        return
      }

      const k0 = cur[0] + (target[0] - cur[0]) * lerpFactor
      const k1 = cur[1] + (target[1] - cur[1]) * lerpFactor

      // Stop when close enough
      if (Math.abs(k0 - target[0]) < 0.1 && Math.abs(k1 - target[1]) < 0.1) {
        self._zoomDistanceRange = target
        self._zoomTarget = null
        self._scheduleRedraw()
        self._dispatchZoom(self._zoomDistanceRange)
        return
      }

      self._zoomDistanceRange = [Math.max(fullMin, k0), Math.min(fullMax, k1)]
      self._scheduleRedraw()
      self._dispatchZoom(self._zoomDistanceRange)
      self._chartZoomRAF = requestAnimationFrame(tickChartZoom)
    }

    svg.on("wheel", function (event) {
      event.preventDefault()
      event.stopPropagation()

      const [mx] = d3.pointer(event, g.node())
      const [d0, d1] =
        self._zoomTarget === "reset"
          ? self._xFullExtent
          : self._zoomTarget || self._zoomDistanceRange || self._xFullExtent
      const [fullMin, fullMax] = self._xFullExtent
      const fullRange = fullMax - fullMin
      const currentRange = d1 - d0
      const mouseDist = Math.max(d0, Math.min(d1, self._xScale.invert(mx)))

      const factor = event.deltaY > 0 ? 1.2 : 1 / 1.2
      const newRange = currentRange * factor

      if (newRange >= fullRange) {
        self._zoomTarget = "reset"
      } else if (newRange < fullRange / 50) {
        return
      } else {
        const ratio = (mouseDist - d0) / currentRange
        let newD0 = mouseDist - newRange * ratio
        let newD1 = mouseDist + newRange * (1 - ratio)
        if (newD0 < fullMin) {
          newD0 = fullMin
          newD1 = fullMin + newRange
        }
        if (newD1 > fullMax) {
          newD1 = fullMax
          newD0 = fullMax - newRange
        }
        self._zoomTarget = [Math.max(fullMin, newD0), Math.min(fullMax, newD1)]
      }

      if (!self._chartZoomRAF)
        self._chartZoomRAF = requestAnimationFrame(tickChartZoom)
    })

    // Interaction overlay for hover and drag-to-pan
    const bisector = d3.bisector((d) => d.distance_m).left
    const overlay = g
      .append("rect")
      .attr("class", "interaction-overlay")
      .attr("width", innerWidth)
      .attr("height", innerHeight)
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .attr("cursor", self._zoomDistanceRange ? "grab" : "crosshair")

    // Hover handling (rAF-gated, uses self._xScale for current scale after redraws)
    overlay.on("mousemove", function (event) {
      if (self._isPanning) return
      if (!hoverData || hoverData.length === 0) return
      const [mx] = d3.pointer(event, this)
      const dist = self._xScale.invert(mx)
      const idx = bisector(hoverData, dist, 1)
      const clampedIdx = Math.max(
        0,
        Math.min(
          hoverData.length - 1,
          self._closestIndex(hoverData, dist, idx),
        ),
      )

      self._pendingHoverIdx = clampedIdx
      if (!self._hoverRAF) {
        self._hoverRAF = requestAnimationFrame(() => {
          self._hoverRAF = null
          const i = self._pendingHoverIdx

          self.crosshair
            .attr("x1", self._xScale(hoverData[i].distance_m))
            .attr("x2", self._xScale(hoverData[i].distance_m))
            .style("display", null)

          self._updateHoverLabels(i)

          self.element.dispatchEvent(
            new CustomEvent("telemetry:hover", {
              bubbles: true,
              detail: { index: i, source: self.element },
            }),
          )
        })
      }
    })

    // Drag-to-pan
    overlay.on("mousedown", function (event) {
      if (!self._zoomDistanceRange) return
      event.preventDefault()
      // Cancel any running smooth-zoom animation so it doesn't fight with drag
      if (self._chartZoomRAF) {
        cancelAnimationFrame(self._chartZoomRAF)
        self._chartZoomRAF = null
        self._zoomTarget = null
      }
      self._panStartX = event.clientX
      self._panStartRange = [...self._zoomDistanceRange]
      self._isPanning = true
      overlay.attr("cursor", "grabbing")
    })

    // Double-click to reset zoom
    svg.on("dblclick", (event) => {
      event.preventDefault()
      self._zoomDistanceRange = null
      self._scheduleRedraw()
      self._dispatchZoom(null)
    })

    // Store references for hover sync and panning
    this._xScale = xScale
    this._innerWidth = innerWidth
    this._innerHeight = innerHeight
    this._bisector = bisector
    this._overlay = overlay
  }

  _dispatchZoom(distanceRange) {
    storeSet("zoomRange", distanceRange)
    this.element.dispatchEvent(
      new CustomEvent("telemetry:zoom", {
        bubbles: true,
        detail: { distanceRange, source: this.element },
      }),
    )
  }

  _makeDistanceFormatter() {
    return (d) => `${Math.round(d)} m`
  }

  // --- Single-lap render ---

  _renderSingle(container) {
    const data = this.telemetry
    if (!data || data.length === 0) {
      container.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No telemetry data</div>'
      return
    }

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const margin = { top: 18, right: 4, bottom: 2, left: 0 }

    // Create Canvas + SVG layout
    const { svg, g, innerWidth, innerHeight } = this._createChartLayout(
      container,
      width,
      height,
      margin,
    )

    const allMetrics = this.metricValue.split(",")
    const allColors = this.colorValue.split(",")
    const metrics = allMetrics.filter((m) => !this.hiddenMetrics.has(m))
    const colors = allMetrics
      .map((m, i) => (this.hiddenMetrics.has(m) ? null : allColors[i]))
      .filter(Boolean)

    const xDomain = this._computeXDomain(data)
    const xScale = d3.scaleLinear().domain(xDomain).range([0, innerWidth])

    // Use full data extent for Y domain (fixed axis during zoom)
    let yMin = Infinity,
      yMax = -Infinity
    metrics.forEach((metric) => {
      data.forEach((d) => {
        const v = d[metric]
        if (v != null && isFinite(v)) {
          if (v < yMin) yMin = v
          if (v > yMax) yMax = v
        }
      })
    })
    if (!isFinite(yMin)) yMin = 0
    if (!isFinite(yMax)) yMax = 1
    const yScale = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .nice()
      .range([innerHeight, 0])

    const xTickFormat = this._makeDistanceFormatter()
    const curveType = this.stepValue ? d3.curveStepAfter : d3.curveMonotoneX

    // SVG: axes, sectors, interaction overlay, crosshair, hover labels
    const xAxisGroup = g.append("g").attr("class", "x-axis")
    const yAxisGroup = g.append("g").attr("class", "y-axis")
    this._updateAxisGroups(
      xAxisGroup,
      yAxisGroup,
      xScale,
      yScale,
      innerHeight,
      xTickFormat,
    )

    const sectorGroup = g.append("g").attr("class", "sectors")
    this._renderSectorLines(sectorGroup, xScale, innerHeight, data)

    this._setupZoomAndBrush(svg, g, innerWidth, innerHeight, xScale, data)

    this.crosshair = this._createCrosshair(g, innerHeight)
    this._hoverState = { type: "single", data, metrics, yScale }
    this._createHoverLabels(g, colors)

    // Pre-compute smoothed data once for canvas rendering
    const smoothedData = this.stepValue ? data : this._smoothData(data, metrics)

    // Store render state for fast redraw
    this._renderState = {
      mode: "single",
      width,
      height,
      margin,
      innerWidth,
      innerHeight,
      data,
      smoothedData,
      metrics,
      colors,
      curveType,
      xScale,
      yScale,
      yDomain: yScale.domain(),
      xTickFormat,
      yTickFormat: null,
      hoverData: data,
      sectorData: data,
      xAxisGroup,
      yAxisGroup,
      sectorGroup,
    }

    // Draw data lines on canvas
    this._drawCanvasData(xScale, yScale, innerWidth, innerHeight)
  }

  // --- Comparison metrics render ---

  _renderComparisonMetrics(container) {
    const laps = this.comparisonLaps || this.lapsValue
    if (!laps || laps.length === 0) {
      container.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No telemetry data</div>'
      return
    }

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const margin = { top: 18, right: 4, bottom: 2, left: 0 }

    const { svg, g, innerWidth, innerHeight } = this._createChartLayout(
      container,
      width,
      height,
      margin,
    )

    const allMetrics = this.hasMetricValue ? this.metricValue.split(",") : []
    const metrics = allMetrics.filter((m) => !this.hiddenMetrics.has(m))
    if (metrics.length === 0) return

    const xDomain = this._computeXDomainMulti(laps)
    const xScale = d3.scaleLinear().domain(xDomain).range([0, innerWidth])

    // Use full data extent for Y domain (fixed axis during zoom)
    let yMin = Infinity,
      yMax = -Infinity
    laps.forEach((lap) => {
      const data = lap.telemetry
      if (!data || data.length === 0) return
      data.forEach((d) => {
        metrics.forEach((metric) => {
          const v = d[metric]
          if (v != null && isFinite(v)) {
            if (v < yMin) yMin = v
            if (v > yMax) yMax = v
          }
        })
      })
    })
    if (!isFinite(yMin)) yMin = 0
    if (!isFinite(yMax)) yMax = 1
    const yScale = d3
      .scaleLinear()
      .domain([yMin, yMax])
      .nice()
      .range([innerHeight, 0])

    const refData = laps[0]?.telemetry || []
    const xTickFormat = this._makeDistanceFormatter()
    const curveType = this.stepValue ? d3.curveStepAfter : d3.curveMonotoneX

    const xAxisGroup = g.append("g").attr("class", "x-axis")
    const yAxisGroup = g.append("g").attr("class", "y-axis")
    this._updateAxisGroups(
      xAxisGroup,
      yAxisGroup,
      xScale,
      yScale,
      innerHeight,
      xTickFormat,
    )

    const sectorGroup = g.append("g").attr("class", "sectors")
    this._renderSectorLines(sectorGroup, xScale, innerHeight, refData)

    this._setupZoomAndBrush(svg, g, innerWidth, innerHeight, xScale, refData)

    this.crosshair = this._createCrosshair(g, innerHeight)
    this._hoverState = { type: "comparison", laps, metrics, yScale }
    const chartColors = this.hasColorValue ? this.colorValue.split(",") : []
    const hoverColors = laps.map((l, i) => {
      const base = chartColors[0] || l.color
      return i > 0 ? this._dimColor(base, 0.5) : base
    })
    this._createHoverLabels(g, hoverColors)

    // Pre-compute smoothed data once for canvas rendering
    const smoothedLaps = this.stepValue
      ? laps
      : laps.map((lap) => ({
          ...lap,
          _smoothed: lap.telemetry
            ? this._smoothData(lap.telemetry, metrics)
            : null,
        }))

    this._renderState = {
      mode: "comparison",
      width,
      height,
      margin,
      innerWidth,
      innerHeight,
      laps,
      smoothedLaps,
      metrics,
      colors: null,
      curveType,
      xScale,
      yScale,
      yDomain: yScale.domain(),
      xTickFormat,
      yTickFormat: null,
      hoverData: refData,
      sectorData: refData,
      xAxisGroup,
      yAxisGroup,
      sectorGroup,
    }

    this._drawCanvasData(xScale, yScale, innerWidth, innerHeight)
  }

  // --- Delta render ---

  _renderDelta(container) {
    let allSeries, refTelemetry, colors

    if (this.comparisonLaps && this.comparisonLaps.length >= 2) {
      refTelemetry = this.comparisonLaps[0].telemetry || []
      colors = this.comparisonLaps.slice(1).map(() => "#ef4444")

      // Build lap-relative time-vs-distance for each lap, then compute delta.
      // Distances are normalized to fractional position [0→1] before interpolation
      // to handle cross-session distance calibration differences (same physical track
      // point may report different absolute distances in different sessions).
      // Note: timestamp_ms is actually in seconds (mapped from session_time f32).
      const refT0 = refTelemetry[0]?.timestamp_ms || 0
      const refMaxDist = refTelemetry[refTelemetry.length - 1].distance_m
      const refFrac = refTelemetry.map((t) => [
        t.distance_m / refMaxDist,
        t.timestamp_ms - refT0,
      ])

      allSeries = this.comparisonLaps.slice(1).map((lap) => {
        const lapTelemetry = lap.telemetry || []
        if (lapTelemetry.length === 0) return []

        const lapT0 = lapTelemetry[0]?.timestamp_ms || 0
        const lapMaxDist = lapTelemetry[lapTelemetry.length - 1].distance_m
        const lapFrac = lapTelemetry.map((t) => [
          t.distance_m / lapMaxDist,
          t.timestamp_ms - lapT0,
        ])

        // Uniform grid in fractional space [0, 1]
        const gridCount = Math.round(refMaxDist)
        if (gridCount <= 0) return []
        const grid = new Float64Array(gridCount + 1)
        for (let i = 0; i <= gridCount; i++) grid[i] = i / gridCount

        // Linear interpolation of time at each fractional grid point
        const interpTime = (frac, gridPts) => {
          const result = new Float64Array(gridPts.length)
          let j = 0
          for (let i = 0; i < gridPts.length; i++) {
            const s = gridPts[i]
            while (j < frac.length - 2 && frac[j + 1][0] < s) j++
            const [d0, t0] = frac[j]
            const [d1, t1] = frac[Math.min(j + 1, frac.length - 1)]
            const span = d1 - d0
            if (Math.abs(span) < 1e-9) {
              result[i] = t0
            } else {
              result[i] =
                t0 + Math.min(1, Math.max(0, (s - d0) / span)) * (t1 - t0)
            }
          }
          return result
        }

        const refInterp = interpTime(refFrac, grid)
        const lapInterp = interpTime(lapFrac, grid)

        // Delta anchored to 0 at start, in reference distance coordinates
        // positive = I'm slower, negative = I'm faster
        const initialDelta = refInterp[0] - lapInterp[0]
        const series = new Array(grid.length)
        for (let i = 0; i < grid.length; i++) {
          series[i] = {
            distance_m: grid[i] * refMaxDist,
            delta_s: refInterp[i] - lapInterp[i] - initialDelta,
          }
        }
        return series
      })
    } else {
      const deltaData = this.deltaValue
      refTelemetry = this.referenceTelemetryValue
      colors = this.hasColorsValue ? this.colorsValue : ["#f97316", "#22c55e"]

      if (!deltaData || !refTelemetry || refTelemetry.length === 0) {
        container.innerHTML =
          '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No delta data</div>'
        return
      }

      const lapIds = Object.keys(deltaData)
      allSeries = lapIds.map((id) => {
        return (deltaData[id] || []).map((d) => ({
          distance_m: d.distance_m,
          delta_s: d.delta_ms / 1000,
        }))
      })
    }

    if (!refTelemetry || refTelemetry.length === 0) {
      container.innerHTML =
        '<div style="display:flex;align-items:center;justify-content:center;width:100%;height:100%;color:var(--app-text-muted);font-size:0.875rem;">No delta data</div>'
      return
    }

    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const margin = { top: 18, right: 4, bottom: 2, left: 0 }

    const { svg, g, innerWidth, innerHeight } = this._createChartLayout(
      container,
      width,
      height,
      margin,
    )

    let xMin = Infinity,
      xMax = -Infinity
    allSeries.forEach((series) => {
      series.forEach((d) => {
        if (d.distance_m < xMin) xMin = d.distance_m
        if (d.distance_m > xMax) xMax = d.distance_m
      })
    })
    if (!isFinite(xMin)) xMin = 0
    if (!isFinite(xMax)) xMax = 1
    this._xFullExtent = [xMin, xMax]
    const xDomain = this._zoomDistanceRange || [xMin, xMax]
    const xScale = d3.scaleLinear().domain(xDomain).range([0, innerWidth])

    // Use full data extent for Y domain (fixed axis during zoom)
    let yMin = Infinity,
      yMax = -Infinity
    allSeries.forEach((series) => {
      series.forEach((d) => {
        if (d.delta_s < yMin) yMin = d.delta_s
        if (d.delta_s > yMax) yMax = d.delta_s
      })
    })
    if (!isFinite(yMin)) yMin = -1
    if (!isFinite(yMax)) yMax = 1

    const yAbsMax = Math.max(Math.abs(yMin), Math.abs(yMax), 0.1)
    const yScale = d3
      .scaleLinear()
      .domain([-yAbsMax, yAbsMax])
      .nice()
      .range([innerHeight, 0])

    const xTickFormat = this._makeDistanceFormatter()
    const yTickFormat = (d) => (d > 0 ? `+${d.toFixed(1)}` : d.toFixed(1))

    const xAxisGroup = g.append("g").attr("class", "x-axis")
    const yAxisGroup = g.append("g").attr("class", "y-axis")
    this._updateAxisGroups(
      xAxisGroup,
      yAxisGroup,
      xScale,
      yScale,
      innerHeight,
      xTickFormat,
      yTickFormat,
    )

    const sectorGroup = g.append("g").attr("class", "sectors")
    this._renderSectorLines(sectorGroup, xScale, innerHeight, refTelemetry)

    this._setupZoomAndBrush(
      svg,
      g,
      innerWidth,
      innerHeight,
      xScale,
      refTelemetry,
    )

    this.crosshair = this._createCrosshair(g, innerHeight)
    this._hoverState = {
      type: "delta",
      refTelemetry,
      series: allSeries,
      yScale,
    }
    this._createHoverLabels(g, colors)

    this._renderState = {
      mode: "delta",
      width,
      height,
      margin,
      innerWidth,
      innerHeight,
      allSeries,
      refTelemetry,
      colors,
      curveType: d3.curveMonotoneX,
      xScale,
      yScale,
      yDomain: yScale.domain(),
      xTickFormat,
      yTickFormat,
      hoverData: refTelemetry,
      sectorData: refTelemetry,
      xAxisGroup,
      yAxisGroup,
      sectorGroup,
    }

    this._drawCanvasData(xScale, yScale, innerWidth, innerHeight)
  }

  // --- Hover handling ---

  onHover(event) {
    const { index } = event.detail

    if (this.comparisonValue && this.deltaModeValue) {
      const refTelemetry =
        this.comparisonLaps?.[0]?.telemetry || this.referenceTelemetryValue
      if (
        !refTelemetry ||
        !this.crosshair ||
        index < 0 ||
        index >= refTelemetry.length
      )
        return
      this.crosshair
        .attr("x1", this._xScale(refTelemetry[index].distance_m))
        .attr("x2", this._xScale(refTelemetry[index].distance_m))
        .style("display", null)
    } else if (this.comparisonValue && this.hasDeltaValue) {
      const refTelemetry = this.referenceTelemetryValue
      if (
        !refTelemetry ||
        !this.crosshair ||
        index < 0 ||
        index >= refTelemetry.length
      )
        return
      this.crosshair
        .attr("x1", this._xScale(refTelemetry[index].distance_m))
        .attr("x2", this._xScale(refTelemetry[index].distance_m))
        .style("display", null)
    } else if (this.comparisonValue) {
      const refData =
        (this.comparisonLaps || this.lapsValue)?.[0]?.telemetry || []
      if (!this.crosshair || index < 0 || index >= refData.length) return
      this.crosshair
        .attr("x1", this._xScale(refData[index].distance_m))
        .attr("x2", this._xScale(refData[index].distance_m))
        .style("display", null)
    } else {
      const data = this.telemetry
      if (!data || !this.crosshair || index < 0 || index >= data.length) return
      this.crosshair
        .attr("x1", this._xScale(data[index].distance_m))
        .attr("x2", this._xScale(data[index].distance_m))
        .style("display", null)
    }

    this._updateHoverLabels(index)
  }

  onLeave() {
    // Keep crosshair visible at last position — never hide
  }

  // --- Sector markers ---

  _getSectorDistances(data) {
    // Prefer new N-sector array values
    if (this.hasSectorDistancesValue && this.sectorDistancesValue.length > 0) {
      return this.sectorDistancesValue
        .filter((d) => d > 0)
        .map((dist, i) => ({
          dist,
          label: `S${i + 2}`,
          color: getSectorColor(i),
        }))
    }

    // Try computing distances from sectorsMs array via telemetry timestamps
    if (
      this.hasSectorsMsValue &&
      this.sectorsMsValue.length > 1 &&
      data.length > 1
    ) {
      const startTime = data[0].timestamp_ms
      if (startTime != null) {
        const boundaries = []
        let cumMs = 0
        // N sectors produce N-1 boundaries (between sector 1-2, 2-3, etc.)
        for (let s = 0; s < this.sectorsMsValue.length - 1; s++) {
          cumMs += this.sectorsMsValue[s]
          const boundaryTime = startTime + cumMs / 1000
          for (let i = 1; i < data.length; i++) {
            if (data[i].timestamp_ms >= boundaryTime) {
              const dist = data[i].distance_m || 0
              if (dist > 0)
                boundaries.push({
                  dist,
                  label: `S${s + 2}`,
                  color: getSectorColor(s),
                })
              break
            }
          }
        }
        if (boundaries.length > 0) return boundaries
      }
    }

    // Fallback: old individual values
    let s2 = this.hasSector2DistanceValue ? this.sector2DistanceValue : 0
    let s3 = this.hasSector3DistanceValue ? this.sector3DistanceValue : 0

    if (
      (!s2 || !s3) &&
      this.hasSector1MsValue &&
      this.hasSector2MsValue &&
      data.length > 1
    ) {
      const startTime = data[0].timestamp_ms
      if (startTime != null) {
        const s1EndTime = startTime + this.sector1MsValue / 1000
        const s2EndTime = s1EndTime + this.sector2MsValue / 1000
        for (let i = 1; i < data.length; i++) {
          const t = data[i].timestamp_ms
          if (!s2 && t >= s1EndTime) s2 = data[i].distance_m || 0
          if (!s3 && t >= s2EndTime) {
            s3 = data[i].distance_m || 0
            break
          }
        }
      }
    }

    const sectors = []
    if (s2 > 0) sectors.push({ dist: s2, label: "S2" })
    if (s3 > 0) sectors.push({ dist: s3, label: "S3" })
    return sectors
  }

  _renderSectorLines(g, xScale, innerHeight, data) {
    const sectors = this._getSectorDistances(data)
    const [xMin, xMax] = xScale.domain()

    sectors.forEach(({ dist, label, color: sectorColor }) => {
      if (dist < xMin || dist > xMax) return
      const x = xScale(dist)
      const color =
        sectorColor || getSectorColors()[label] || _cssVar("--app-text-faint")

      g.append("line")
        .attr("x1", x)
        .attr("x2", x)
        .attr("y1", 0)
        .attr("y2", innerHeight)
        .attr("stroke", _cssVar("--app-border-strong"))
        .attr("stroke-width", 1)
        .attr("stroke-dasharray", "4,3")

      // Use Canvas measureText instead of getBBox() to avoid forced layout
      const sectorTextWidth = getTextWidth(label, 8, "bold")
      const sectorLabelH = 10 // approximate line height for 8px font
      const sectorLabelY = innerHeight + 14

      g.append("rect")
        .attr("fill", color)
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("x", x - sectorTextWidth / 2 - 3)
        .attr("y", sectorLabelY - sectorLabelH / 2 - 1)
        .attr("width", sectorTextWidth + 6)
        .attr("height", sectorLabelH + 2)

      g.append("text")
        .attr("x", x)
        .attr("y", sectorLabelY)
        .attr("text-anchor", "middle")
        .attr("dominant-baseline", "central")
        .attr("fill", "white")
        .attr("font-size", "8px")
        .attr("font-weight", "bold")
        .text(label)
    })
  }

  // --- Utilities ---

  _createCrosshair(g, innerHeight) {
    return g
      .append("line")
      .attr("y1", 0)
      .attr("y2", innerHeight)
      .attr("stroke", _cssVar("--app-text-faint"))
      .attr("stroke-width", 1.5)
      .style("pointer-events", "none")
      .style("display", "none")
  }

  _createHoverLabels(g, colors) {
    this._hoverLabels = colors.map((color) => {
      const group = g
        .append("g")
        .style("pointer-events", "none")
        .style("display", "none")

      const rect = group
        .append("rect")
        .attr("rx", 4)
        .attr("ry", 4)
        .attr("fill", color)

      const text = group
        .append("text")
        .attr("fill", "#fff")
        .attr("font-size", "10px")
        .attr("font-weight", "600")

      return { group, rect, text }
    })
  }

  _updateHoverLabels(index) {
    if (!this._hoverLabels || !this._hoverState) return

    const { type, yScale } = this._hoverState

    const unit = this._extractUnit()

    if (type === "single") {
      const { data, metrics } = this._hoverState
      if (!data || index < 0 || index >= data.length) {
        this._hideHoverLabels()
        return
      }
      const d = data[index]
      const x = this._xScale(d.distance_m)

      metrics.forEach((metric, i) => {
        const label = this._hoverLabels[i]
        if (!label) return
        const val = d[metric]
        if (val == null || !isFinite(val)) {
          label.group.style("display", "none")
          return
        }
        this._positionLabel(
          label,
          x,
          yScale(val),
          this._formatHoverValue(val, unit),
        )
      })
    } else if (type === "comparison") {
      const { laps, metrics } = this._hoverState
      const refData = laps[0]?.telemetry
      if (!refData || index < 0 || index >= refData.length) {
        this._hideHoverLabels()
        return
      }
      const refDist = refData[index].distance_m
      const x = this._xScale(refDist)
      const bisector = d3.bisector((d) => d.distance_m).left
      const metric = metrics[0]

      laps.forEach((lap, i) => {
        const label = this._hoverLabels[i]
        if (!label) return
        const lapData = lap.telemetry
        if (!lapData || lapData.length === 0) {
          label.group.style("display", "none")
          return
        }

        let idx = index
        if (i > 0) {
          const bi = bisector(lapData, refDist, 1)
          idx = this._closestIndex(lapData, refDist, bi)
        }

        const val = lapData[idx]?.[metric]
        if (val == null || !isFinite(val)) {
          label.group.style("display", "none")
          return
        }
        this._positionLabel(
          label,
          x,
          yScale(val),
          this._formatHoverValue(val, unit),
        )
      })
    } else if (type === "delta") {
      const { refTelemetry, series } = this._hoverState
      if (!refTelemetry || index < 0 || index >= refTelemetry.length) {
        this._hideHoverLabels()
        return
      }
      const refDist = refTelemetry[index].distance_m
      const x = this._xScale(refDist)
      const bisector = d3.bisector((d) => d.distance_m).left

      series.forEach((s, i) => {
        const label = this._hoverLabels[i]
        if (!label || s.length === 0) {
          if (label) label.group.style("display", "none")
          return
        }
        const bi = bisector(s, refDist, 1)
        const closest = this._closestIndex(s, refDist, bi)
        const val = s[closest]?.delta_s
        if (val == null || !isFinite(val)) {
          label.group.style("display", "none")
          return
        }
        const formatted = (val > 0 ? "+" : "") + val.toFixed(3) + "s"
        // Color label to match the line: red when losing time, green when gaining
        label.rect.attr("fill", val >= 0 ? "#ef4444" : "#22c55e")
        this._positionLabel(label, x, yScale(val), formatted)
      })
    }
  }

  _positionLabel(label, x, y, text) {
    const PADDING_X = 6
    const PADDING_Y = 3
    const OFFSET_X = 8
    const FONT_SIZE = 10
    const LINE_HEIGHT = 13 // approximate line height for 10px font

    const clampedY = Math.max(8, Math.min(this._innerHeight - 8, y))
    const goRight = x < this._innerWidth * 0.75

    const textX = goRight ? x + OFFSET_X + PADDING_X : x - OFFSET_X - PADDING_X
    label.text
      .text(text)
      .attr("text-anchor", goRight ? "start" : "end")
      .attr("x", textX)
      .attr("y", clampedY)
      .attr("dominant-baseline", "central")

    // Use Canvas measureText instead of getBBox() to avoid forced layout
    const textWidth = getTextWidth(text, FONT_SIZE, "600")
    const rectX = goRight ? textX - PADDING_X : textX - textWidth - PADDING_X
    label.rect
      .attr("x", rectX)
      .attr("y", clampedY - LINE_HEIGHT / 2 - PADDING_Y)
      .attr("width", textWidth + PADDING_X * 2)
      .attr("height", LINE_HEIGHT + PADDING_Y * 2)

    label.group.style("display", null)
  }

  _hideHoverLabels() {
    if (!this._hoverLabels) return
    this._hoverLabels.forEach((l) => l.group.style("display", "none"))
  }

  _formatHoverValue(val, unit) {
    const v = val * (this.displayScaleValue || 1)
    let formatted
    if (Math.abs(v) >= 1e6) formatted = d3.format(".3~s")(v)
    else if (Math.abs(v) >= 1e3) formatted = d3.format(".3~s")(v)
    else if (Number.isInteger(v)) formatted = v.toString()
    else if (Math.abs(v) >= 100) formatted = Math.round(v).toString()
    else if (Math.abs(v) >= 10) formatted = v.toFixed(1)
    else formatted = v.toFixed(2)

    return unit ? `${formatted} ${unit}` : formatted
  }

  _extractUnit() {
    const match = this.labelValue?.match(/\(([^)]+)\)/)
    return match ? match[1] : ""
  }

  _dimColor(hex, alpha) {
    // Return an rgba version of the hex color with reduced opacity
    const r = parseInt(hex.slice(1, 3), 16)
    const g = parseInt(hex.slice(3, 5), 16)
    const b = parseInt(hex.slice(5, 7), 16)
    return `rgba(${r},${g},${b},${alpha})`
  }

  _updateLegend() {
    if (!this.hasLegendValue || !this.hasMetricValue) return
    const metrics = this.metricValue.split(",")
    if (metrics.length <= 1) return

    const labels = this.legendValue.split(",")
    const colors = this.hasColorValue ? this.colorValue.split(",") : []

    let container = this.hasLegendTarget ? this.legendTarget : null
    if (!container) {
      container = this.element.querySelector(
        "[data-telemetry-chart-target='legend']",
      )
    }
    if (!container) return

    container.innerHTML = ""
    container.className =
      "absolute bottom-1.5 right-3 z-10 flex items-center gap-3"

    labels.forEach((label, i) => {
      const metric = metrics[i]
      if (!metric) return

      const isHidden = this.hiddenMetrics.has(metric)
      const color = colors[i] || colors[0] || _cssVar("--app-text-muted")

      const item = document.createElement("button")
      item.type = "button"
      item.className =
        "flex items-center gap-1 text-xs transition-colors cursor-pointer select-none"
      item.style.color = isHidden
        ? _cssVar("--app-border-strong")
        : _cssVar("--app-text-muted")

      const dot = document.createElement("span")
      dot.className = "inline-block w-2 h-2 rounded-full transition-colors"
      dot.style.backgroundColor = isHidden
        ? _cssVar("--app-border-strong")
        : color

      const text = document.createElement("span")
      text.textContent = label
      if (isHidden) text.style.textDecoration = "line-through"

      item.appendChild(dot)
      item.appendChild(text)

      item.addEventListener("click", () => {
        if (this.hiddenMetrics.has(metric)) {
          this.hiddenMetrics.delete(metric)
        } else {
          const visibleCount = metrics.filter(
            (m) => !this.hiddenMetrics.has(m),
          ).length
          if (visibleCount <= 1) return
          this.hiddenMetrics.add(metric)
        }
        this._updateLegend()
        this.render()
      })

      container.appendChild(item)
    })
  }

  // --- Chart layout (Canvas + SVG overlay) ---

  _createChartLayout(container, width, height, margin) {
    const innerWidth = width - margin.left - margin.right
    const innerHeight = height - margin.top - margin.bottom

    const wrapper = document.createElement("div")
    wrapper.style.cssText = "position:relative;width:100%;height:100%"

    // Canvas for data lines (positioned at inner chart area)
    const dpr = window.devicePixelRatio || 1
    const canvas = document.createElement("canvas")
    canvas.style.cssText = `position:absolute;left:${margin.left}px;top:${margin.top}px;width:${innerWidth}px;height:${innerHeight}px`
    canvas.width = Math.round(innerWidth * dpr)
    canvas.height = Math.round(innerHeight * dpr)
    const ctx = canvas.getContext("2d")
    ctx.scale(dpr, dpr)
    wrapper.appendChild(canvas)

    // SVG overlay for axes, sectors, crosshair, hover labels, interaction
    const svg = d3
      .select(wrapper)
      .append("svg")
      .attr("width", width)
      .attr("height", height)
      .style("position", "absolute")
      .style("top", "0")
      .style("left", "0")
    const g = svg
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`)

    container.appendChild(wrapper)

    this._chartCanvas = canvas
    this._chartCtx = ctx
    this._dpr = dpr

    return { svg, g, innerWidth, innerHeight }
  }

  _getAxisTickColor() {
    if (!this.hasAxisColorValue || !this.axisColorValue)
      return _cssVar("--app-text-faint")
    const hex = this.axisColorValue.replace("#", "")
    const r = parseInt(hex.substring(0, 2), 16)
    const g = parseInt(hex.substring(2, 4), 16)
    const b = parseInt(hex.substring(4, 6), 16)
    return `rgba(${r},${g},${b},0.45)`
  }

  _formatYTick(d, yTickFormat) {
    if (yTickFormat) return yTickFormat(d)
    const scale = this.displayScaleValue || 1
    const v = d * scale
    const abs = Math.abs(v)
    if (abs === 0) return "0"
    if (abs >= 1e6) return d3.format(".3~s")(v)
    if (abs >= 1e3) return d3.format(".3~s")(v)
    if (abs >= 100) return Math.round(v).toString()
    if (abs >= 10) return v.toFixed(1)
    if (abs >= 1) return v.toFixed(1)
    return v.toFixed(2)
  }

  _updateAxisGroups(
    xGroup,
    yGroup,
    xScale,
    yScale,
    innerHeight,
    xTickFormat,
    yTickFormat,
  ) {
    const tickColor = this._getAxisTickColor()
    const innerWidth = xScale.range()[1] - xScale.range()[0]

    // X axis — removed entirely
    xGroup.selectAll("*").remove()

    // Y axis — no axis, just gridlines + inside labels
    yGroup.selectAll("*").remove()
    const ticks = yScale.ticks(3)

    ticks.forEach((d) => {
      const y = yScale(d)
      // Gridline
      yGroup
        .append("line")
        .attr("x1", 0)
        .attr("x2", innerWidth)
        .attr("y1", y)
        .attr("y2", y)
        .attr("stroke", _cssVar("--app-text-secondary"))
        .attr("stroke-opacity", 0.4)

      // Label inside chart — left edge with small padding
      yGroup
        .append("text")
        .attr("x", 4)
        .attr("y", y - 4)
        .attr("fill", tickColor)
        .attr("font-size", "9px")
        .text(this._formatYTick(d, yTickFormat))
    })
  }

  // --- Canvas data drawing ---

  _drawCanvasData(xScale, yScale, innerWidth, innerHeight) {
    const rs = this._renderState
    if (!rs || !this._chartCtx) return

    const ctx = this._chartCtx
    const dpr = this._dpr

    // Clear canvas
    ctx.setTransform(1, 0, 0, 1, 0, 0)
    ctx.clearRect(0, 0, this._chartCanvas.width, this._chartCanvas.height)

    // Resize canvas if dimensions changed (e.g., during redraw after axis rescale)
    const canvasW = Math.round(innerWidth * dpr)
    const canvasH = Math.round(innerHeight * dpr)
    if (
      this._chartCanvas.width !== canvasW ||
      this._chartCanvas.height !== canvasH
    ) {
      this._chartCanvas.width = canvasW
      this._chartCanvas.height = canvasH
      this._chartCanvas.style.width = `${innerWidth}px`
      this._chartCanvas.style.height = `${innerHeight}px`
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0)

    if (rs.mode === "single") {
      this._drawSingleCanvas(ctx, xScale, yScale, innerWidth, rs)
    } else if (rs.mode === "comparison") {
      this._drawComparisonCanvas(ctx, xScale, yScale, innerWidth, rs)
    } else if (rs.mode === "delta") {
      this._drawDeltaCanvas(ctx, xScale, yScale, innerWidth, rs)
    }
  }

  _drawSingleCanvas(ctx, xScale, yScale, innerWidth, rs) {
    const { smoothedData, metrics, colors, curveType } = rs
    const renderData = this._downsampleForRender(
      smoothedData,
      innerWidth,
      metrics,
    )

    metrics.forEach((metric, i) => {
      const lineGen = d3
        .line()
        .x((d) => xScale(d.distance_m))
        .y((d) => yScale(d[metric] != null ? d[metric] : 0))
        .curve(curveType)
        .defined((d) => d[metric] != null && isFinite(d[metric]))
        .context(ctx)

      ctx.beginPath()
      lineGen(renderData)
      ctx.strokeStyle = colors[i] || colors[0]
      ctx.lineWidth = 1.5
      ctx.stroke()
    })
  }

  _drawComparisonCanvas(ctx, xScale, yScale, innerWidth, rs) {
    const { smoothedLaps, metrics, curveType } = rs
    const chartColors = this.hasColorValue ? this.colorValue.split(",") : []

    smoothedLaps.forEach((lap, lapIndex) => {
      const data = lap._smoothed || lap.telemetry
      if (!data || data.length === 0) return
      const isComparison = lapIndex > 0

      const renderData = this._downsampleForRender(data, innerWidth, metrics)
      metrics.forEach((metric, metricIndex) => {
        const lineGen = d3
          .line()
          .x((d) => xScale(d.distance_m))
          .y((d) => yScale(d[metric] != null ? d[metric] : 0))
          .curve(curveType)
          .defined((d) => d[metric] != null && isFinite(d[metric]))
          .context(ctx)

        ctx.beginPath()
        lineGen(renderData)

        const baseColor =
          chartColors[metricIndex] || chartColors[0] || lap.color
        ctx.strokeStyle = baseColor
        if (isComparison) {
          ctx.globalAlpha = 0.45
          ctx.setLineDash([6, 4])
        } else {
          ctx.globalAlpha = 0.9
          ctx.setLineDash([])
        }
        ctx.lineWidth = 1.5
        ctx.stroke()
        ctx.globalAlpha = 1
        ctx.setLineDash([])
      })
    })
  }

  _drawDeltaCanvas(ctx, xScale, yScale, innerWidth, rs) {
    const { allSeries } = rs
    const dsTarget = Math.max(500, innerWidth * 2)
    const zeroY = yScale(0)

    // Zero line — subtle solid baseline
    ctx.beginPath()
    ctx.moveTo(0, zeroY)
    ctx.lineTo(innerWidth, zeroY)
    ctx.strokeStyle = "rgba(255,255,255,0.12)"
    ctx.lineWidth = 1
    ctx.stroke()

    allSeries.forEach((series) => {
      if (series.length === 0) return
      const renderSeries = this._lttb(series, dsTarget, ["delta_s"])

      // Filled area — red above zero (slower), green below (faster)
      const areaAbove = d3
        .area()
        .x((d) => xScale(d.distance_m))
        .y0(zeroY)
        .y1((d) => (d.delta_s > 0 ? yScale(d.delta_s) : zeroY))
        .curve(d3.curveMonotoneX)
        .context(ctx)
      ctx.beginPath()
      areaAbove(renderSeries)
      ctx.fillStyle = "rgba(239, 68, 68, 0.18)"
      ctx.fill()

      const areaBelow = d3
        .area()
        .x((d) => xScale(d.distance_m))
        .y0(zeroY)
        .y1((d) => (d.delta_s < 0 ? yScale(d.delta_s) : zeroY))
        .curve(d3.curveMonotoneX)
        .context(ctx)
      ctx.beginPath()
      areaBelow(renderSeries)
      ctx.fillStyle = "rgba(34, 197, 94, 0.18)"
      ctx.fill()

      // Two-tone line: red segments above zero, green below
      for (let j = 0; j < renderSeries.length - 1; j++) {
        const d0 = renderSeries[j]
        const d1 = renderSeries[j + 1]
        const x0 = xScale(d0.distance_m)
        const x1 = xScale(d1.distance_m)
        const y0 = yScale(d0.delta_s)
        const y1 = yScale(d1.delta_s)

        // If segment crosses zero, split it
        if (d0.delta_s >= 0 !== d1.delta_s >= 0) {
          const t = d0.delta_s / (d0.delta_s - d1.delta_s)
          const xMid = x0 + t * (x1 - x0)
          // First half
          ctx.beginPath()
          ctx.moveTo(x0, y0)
          ctx.lineTo(xMid, zeroY)
          ctx.strokeStyle = d0.delta_s >= 0 ? "#ef4444" : "#22c55e"
          ctx.lineWidth = 1.5
          ctx.stroke()
          // Second half
          ctx.beginPath()
          ctx.moveTo(xMid, zeroY)
          ctx.lineTo(x1, y1)
          ctx.strokeStyle = d1.delta_s >= 0 ? "#ef4444" : "#22c55e"
          ctx.lineWidth = 1.5
          ctx.stroke()
        } else {
          ctx.beginPath()
          ctx.moveTo(x0, y0)
          ctx.lineTo(x1, y1)
          ctx.strokeStyle = d0.delta_s >= 0 ? "#ef4444" : "#22c55e"
          ctx.lineWidth = 1.5
          ctx.stroke()
        }
      }
    })
  }

  // --- Fast redraw (canvas + axes, no DOM rebuild) ---

  _redraw() {
    const rs = this._renderState
    if (!rs) return

    const container = this.canvasTarget
    const width = container.clientWidth
    const height = container.clientHeight
    if (width === 0 || height === 0) return

    const dimensionsChanged = width !== rs.width || height !== rs.height

    // Update dimensions in render state and resize SVG/canvas in-place
    if (dimensionsChanged) {
      const { margin } = rs
      const innerWidth = width - margin.left - margin.right
      const innerHeight = height - margin.top - margin.bottom

      rs.width = width
      rs.height = height
      rs.innerWidth = innerWidth
      rs.innerHeight = innerHeight

      // Resize SVG overlay
      const svg = container.querySelector("svg")
      if (svg) {
        svg.setAttribute("width", width)
        svg.setAttribute("height", height)
      }

      // Resize canvas element
      if (this._chartCanvas) {
        const dpr = this._dpr
        this._chartCanvas.style.width = `${innerWidth}px`
        this._chartCanvas.style.height = `${innerHeight}px`
        this._chartCanvas.width = Math.round(innerWidth * dpr)
        this._chartCanvas.height = Math.round(innerHeight * dpr)
        this._chartCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
      }

      // Resize interaction overlay
      const overlay = container.querySelector(".interaction-overlay")
      if (overlay) {
        overlay.setAttribute("width", innerWidth)
        overlay.setAttribute("height", innerHeight)
      }
    }

    const { innerWidth, innerHeight, sectorData, xTickFormat, yTickFormat } = rs

    // Recompute X scale for current zoom; reuse cached Y domain (fixed during zoom)
    let xScale
    if (rs.mode === "single") {
      const xDomain = this._computeXDomain(rs.data)
      xScale = d3.scaleLinear().domain(xDomain).range([0, innerWidth])
    } else if (rs.mode === "comparison") {
      const xDomain = this._computeXDomainMulti(rs.laps)
      xScale = d3.scaleLinear().domain(xDomain).range([0, innerWidth])
    } else if (rs.mode === "delta") {
      const xDomain = this._zoomDistanceRange || this._xFullExtent
      xScale = d3.scaleLinear().domain(xDomain).range([0, innerWidth])
    }
    const yScale = d3.scaleLinear().domain(rs.yDomain).range([innerHeight, 0])

    // Update stored scales
    this._xScale = xScale
    rs.xScale = xScale
    rs.yScale = yScale

    // Update axes in place
    this._updateAxisGroups(
      rs.xAxisGroup,
      rs.yAxisGroup,
      xScale,
      yScale,
      innerHeight,
      xTickFormat,
      yTickFormat,
    )

    // Update sector lines
    rs.sectorGroup.selectAll("*").remove()
    this._renderSectorLines(rs.sectorGroup, xScale, innerHeight, sectorData)

    // Redraw canvas data
    this._drawCanvasData(xScale, yScale, innerWidth, innerHeight)

    // Update hover state yScale
    if (this._hoverState) this._hoverState.yScale = yScale

    // Update overlay cursor
    if (this._overlay) {
      this._overlay.attr(
        "cursor",
        this._zoomDistanceRange ? "grab" : "crosshair",
      )
    }
  }

  _scheduleRedraw() {
    if (this._redrawRAF || this._renderRAF) return
    this._redrawRAF = requestAnimationFrame(() => {
      this._redrawRAF = null
      if (this._renderState) {
        this._redraw()
      } else {
        this.render()
      }
    })
  }

  // --- Performance: downsampling ---

  _scheduleRender() {
    if (this._renderRAF) return
    if (this._redrawRAF) {
      cancelAnimationFrame(this._redrawRAF)
      this._redrawRAF = null
    }
    this._renderRAF = requestAnimationFrame(() => {
      this._renderRAF = null
      this.render()
    })
  }

  // LTTB (Largest Triangle Three Buckets) downsampling.
  // Reduces data points while preserving visual shape.
  _lttb(data, threshold, yKeys) {
    if (threshold >= data.length || threshold < 3) return data

    const sampled = [data[0]]
    const bucketSize = (data.length - 2) / (threshold - 2)
    let a = 0

    for (let i = 0; i < threshold - 2; i++) {
      const avgStart = Math.floor((i + 1) * bucketSize) + 1
      let avgEnd = Math.floor((i + 2) * bucketSize) + 1
      if (avgEnd > data.length - 1) avgEnd = data.length - 1
      const avgLen = avgEnd - avgStart + 1

      let avgX = 0
      const avgYs = yKeys.map(() => 0)
      for (let j = avgStart; j <= avgEnd; j++) {
        avgX += data[j].distance_m
        yKeys.forEach((yk, ki) => {
          avgYs[ki] += data[j][yk] || 0
        })
      }
      avgX /= avgLen
      avgYs.forEach((_, ki) => {
        avgYs[ki] /= avgLen
      })

      const rangeStart = Math.floor(i * bucketSize) + 1
      let rangeEnd = Math.floor((i + 1) * bucketSize) + 1
      if (rangeEnd > data.length - 1) rangeEnd = data.length - 1

      const ax = data[a].distance_m
      const aYs = yKeys.map((yk) => data[a][yk] || 0)
      let maxArea = -1
      let maxIdx = rangeStart

      for (let j = rangeStart; j < rangeEnd; j++) {
        let totalArea = 0
        yKeys.forEach((yk, ki) => {
          totalArea += Math.abs(
            (ax - avgX) * ((data[j][yk] || 0) - aYs[ki]) -
              (ax - data[j].distance_m) * (avgYs[ki] - aYs[ki]),
          )
        })
        if (totalArea > maxArea) {
          maxArea = totalArea
          maxIdx = j
        }
      }

      sampled.push(data[maxIdx])
      a = maxIdx
    }

    sampled.push(data[data.length - 1])
    return sampled
  }

  _downsampleForRender(data, innerWidth, metrics) {
    const target = Math.max(500, innerWidth * 2)
    if (data.length <= target) return data

    if (this.stepValue) {
      return this._downsampleStep(data, target, metrics)
    }

    return this._lttb(data, target, metrics)
  }

  _downsampleStep(data, target, metrics) {
    if (data.length <= target) return data

    // Keep all points where a metric value changes
    const kept = [0]
    for (let i = 1; i < data.length; i++) {
      for (const m of metrics) {
        if (data[i][m] !== data[i - 1][m]) {
          kept.push(i)
          break
        }
      }
    }
    if (kept[kept.length - 1] !== data.length - 1) kept.push(data.length - 1)

    if (kept.length <= target) return kept.map((i) => data[i])

    // Too many transitions: fall back to stride
    const stride = Math.ceil(data.length / target)
    const result = []
    for (let i = 0; i < data.length; i += stride) result.push(data[i])
    if (result[result.length - 1] !== data[data.length - 1])
      result.push(data[data.length - 1])
    return result
  }

  // Light 3-point weighted average [0.25, 0.5, 0.25] to smooth 5Hz data
  _smoothData(data, metrics) {
    if (data.length < 3) return data
    const result = data.map((d) => ({ ...d }))
    for (const metric of metrics) {
      for (let i = 1; i < result.length - 1; i++) {
        const prev = data[i - 1][metric]
        const curr = data[i][metric]
        const next = data[i + 1][metric]
        if (prev != null && curr != null && next != null) {
          result[i][metric] = prev * 0.25 + curr * 0.5 + next * 0.25
        }
      }
    }
    return result
  }

  _closestIndex(data, value, bisectIndex) {
    if (bisectIndex >= data.length) return data.length - 1
    if (bisectIndex <= 0) return 0
    const d0 = data[bisectIndex - 1]
    const d1 = data[bisectIndex]
    return value - d0.distance_m > d1.distance_m - value
      ? bisectIndex
      : bisectIndex - 1
  }

  _closestPoint(points, value, bisectIndex) {
    if (points.length === 0) return null
    if (bisectIndex >= points.length) return points[points.length - 1]
    if (bisectIndex <= 0) return points[0]
    const d0 = points[bisectIndex - 1]
    const d1 = points[bisectIndex]
    return value - d0[0] > d1[0] - value ? d1 : d0
  }
}
